import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select } from "@/components/ui/select"
import { HelpHint } from "@/components/ui/help-hint"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { EscalationReasonRow, ESCALATION_GRID_COLS } from "@/components/domain/escalation-reason-row"
import { MergeReasonDialog } from "@/components/domain/merge-reason-dialog"
import { cn } from "@/lib/cn"
import type {
  EscalationReason,
  EscalationReasonsIndexProps,
  UpdateReasonBody,
} from "@/types/agent_escalation_reason"

// Single-page escalation reason workspace. Mirrors /topics — operator picks
// an agent at the top, three sections render the lifecycle:
//   Pending review → Live → Archived.
// All mutations are Inertia router calls (CSRF + flash + page-state refresh
// handled). URL convention matches Portal::TopicsController: flat resource
// `/escalation_reasons` scoped via ?agent_id=N. (NOTE: Wave 2a may finalize
// the URL — this page assumes the topics-style flat-resource convention.)

const BASE = "/escalation_reasons"

type LiveSort = "timesUsedDesc" | "timesUsedAsc" | "nameAsc"

// Column header row. Same grid template as EscalationReasonRow so the
// columns line up visually across all three sections.
function ColumnHeaders({ usedLabel = "Used" }: { usedLabel?: string }) {
  return (
    <div
      className={cn(
        ESCALATION_GRID_COLS,
        "border-b border-border bg-app-panel2/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-app-muted",
      )}
    >
      <div />
      <div>Category name</div>
      <div>Description</div>
      <div>{usedLabel}</div>
      <div>Provenance</div>
      <div className="text-right">Actions</div>
    </div>
  )
}

export default function AgentEscalationReasonsIndex({
  agent,
  agents,
  pending,
  live,
  archived,
}: EscalationReasonsIndexProps) {
  const [mergeSource, setMergeSource] = useState<EscalationReason | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [liveSort, setLiveSort] = useState<LiveSort>("timesUsedDesc")
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ name: "", key: "", description: "" })
  const [submitting, setSubmitting] = useState(false)

  const handleAgentChange = (agentId: number) => {
    router.get(BASE, { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  const allReasons = useMemo(
    () => [...pending, ...live, ...archived],
    [pending, live, archived],
  )

  const liveSorted = useMemo(() => {
    const rows = [...live]
    rows.sort((a, b) => {
      if (liveSort === "nameAsc") return a.name.localeCompare(b.name)
      if (liveSort === "timesUsedAsc") return a.timesUsed - b.timesUsed
      return b.timesUsed - a.timesUsed
    })
    return rows
  }, [live, liveSort])

  // Mutations — every endpoint is a redirect-back from the controller, so
  // Inertia auto-refreshes props after each call.
  const patchReason = (id: number, body: UpdateReasonBody) => {
    router.patch(`${BASE}/${id}`, { agent_escalation_reason: body }, { preserveScroll: true })
  }

  const approve = (r: EscalationReason) => patchReason(r.id, { status: "live" })
  const archive = (r: EscalationReason) => patchReason(r.id, { status: "archived" })
  const restore = (r: EscalationReason) => patchReason(r.id, { status: "live" })
  const rename  = (r: EscalationReason, name: string) => patchReason(r.id, { name })
  const editDesc = (r: EscalationReason, description: string) =>
    patchReason(r.id, { description })

  const confirmMerge = (targetId: number) => {
    if (!mergeSource) return
    router.post(`${BASE}/${mergeSource.id}/merge_into`, { target_id: targetId }, {
      preserveScroll: true,
      onFinish: () => setMergeSource(null),
    })
  }

  const submitCreate = () => {
    const name = draft.name.trim()
    const key  = draft.key.trim()
    const description = draft.description.trim()
    if (!name || !key || !description) return
    setSubmitting(true)
    router.post(BASE, {
      agent_id: agent.id,
      agent_escalation_reason: { name, key, description, status: "live" },
    }, {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setCreating(false)
        setDraft({ name: "", key: "", description: "" })
      },
    })
  }

  const mergeCandidates = useMemo(() => {
    if (!mergeSource) return []
    return allReasons.filter((r) => r.id !== mergeSource.id)
  }, [allReasons, mergeSource])

  return (
    <AppShell>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <PageHeader
            eyebrow="Agents"
            title="Escalation Reasons"
            subtitle="Why conversations needed a human."
            actions={<Button onClick={() => setCreating(true)}>+ New reason</Button>}
          />
        </div>
        <div className="pt-9">
          <HelpHint
            heading="What are escalation reasons?"
            body={
              <>
                <p>
                  Categorical buckets answering <em>why</em> a convo needed a human
                  — distinct from topics (which answer <em>what</em> the convo is about).
                </p>
                <p className="mt-2">
                  Haiku proposes new reasons; you approve, rename, merge, or archive.
                  Live reasons feed analytics and the classifier prompt.
                </p>
              </>
            }
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs uppercase tracking-wide text-app-muted">Agent</label>
        <div className="min-w-[220px]">
          <Select<number>
            value={{ value: agent.id, label: agent.name }}
            onChange={(o) => o && handleAgentChange(o.value)}
            options={agents.map((a) => ({ value: a.id, label: a.name }))}
            isSearchable={agents.length > 6}
          />
        </div>
      </div>

      {/* Pending review — sticky-prominent at the top, red-tinted. */}
      <Card className="sticky top-2 z-10 border-brand-red/30 ring-1 ring-brand-red/10">
        <header className="flex items-center justify-between border-b border-brand-red/30 bg-brand-red/5 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-brand-red">Pending review</h2>
            <p className="text-xs text-app-muted">
              {pending.length === 0
                ? "Nothing to review."
                : `${pending.length} proposed by the classifier.`}
            </p>
          </div>
        </header>
        {pending.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-app-muted">
            Nothing to review.
          </div>
        ) : (
          <div>
            <ColumnHeaders />
            {pending.map((r) => (
              <EscalationReasonRow
                key={r.id}
                reason={r}
                onApprove={() => approve(r)}
                onMerge={() => setMergeSource(r)}
                onArchive={() => archive(r)}
                onRename={(name) => rename(r, name)}
                onEditDescription={(desc) => editDesc(r, desc)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Live reasons. */}
      <Card className="border-status-success/40 ring-1 ring-status-success/10">
        <header className="flex items-center justify-between border-b border-status-success/30 bg-status-success/5 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-status-success">Live</h2>
            <p className="text-xs text-app-muted">
              {live.length === 0 ? "No live reasons yet." : `${live.length} active.`}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-app-muted">
            <span>Sort by</span>
            <div className="min-w-[160px]">
              <Select<LiveSort>
                value={
                  liveSort === "nameAsc"        ? { value: "nameAsc",        label: "Name A→Z" } :
                  liveSort === "timesUsedAsc"   ? { value: "timesUsedAsc",   label: "Least used" } :
                                                  { value: "timesUsedDesc",  label: "Most used" }
                }
                onChange={(o) => o && setLiveSort(o.value)}
                options={[
                  { value: "timesUsedDesc", label: "Most used" },
                  { value: "timesUsedAsc",  label: "Least used" },
                  { value: "nameAsc",       label: "Name A→Z" },
                ]}
                isSearchable={false}
              />
            </div>
          </div>
        </header>
        {liveSorted.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-app-muted">
            No live reasons yet — approve a pending one or create your own.
          </div>
        ) : (
          <div>
            <ColumnHeaders />
            {liveSorted.map((r) => (
              <EscalationReasonRow
                key={r.id}
                reason={r}
                onMerge={() => setMergeSource(r)}
                onArchive={() => archive(r)}
                onRename={(name) => rename(r, name)}
                onEditDescription={(desc) => editDesc(r, desc)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Archived (collapsed by default). */}
      <Card>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className="flex w-full items-center justify-between border-b border-border px-4 py-3 text-left transition hover:bg-white/5"
        >
          <div>
            <h2 className="text-sm font-semibold text-app-text">Archived</h2>
            <p className="text-xs text-app-muted">
              {archived.length === 0
                ? "No archived reasons."
                : `${archived.length} hidden from the classifier.`}
            </p>
          </div>
          <span className={cn("text-app-muted transition", showArchived && "rotate-90")}>›</span>
        </button>
        {showArchived && (
          archived.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-app-muted">
              Nothing archived yet.
            </div>
          ) : (
            <div>
              <ColumnHeaders usedLabel="Used (before)" />
              {archived.map((r) => (
                <EscalationReasonRow
                  key={r.id}
                  reason={r}
                  onRestore={() => restore(r)}
                  onMerge={() => setMergeSource(r)}
                />
              ))}
            </div>
          )
        )}
      </Card>

      <MergeReasonDialog
        open={mergeSource !== null}
        source={mergeSource}
        candidates={mergeCandidates}
        onClose={() => setMergeSource(null)}
        onConfirm={confirmMerge}
      />

      <Dialog open={creating} onClose={() => !submitting && setCreating(false)} size="md">
        <DialogHeader title="New escalation reason" subtitle="Created live — skips the review queue." />
        <DialogBody>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Name</label>
            <Input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Bug in cashout flow"
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Key</label>
            <Input
              type="text"
              value={draft.key}
              onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
              placeholder="bug_in_cashout_flow"
              className="w-full font-mono"
            />
            <p className="text-[11px] text-app-muted">Lowercase letters, digits, underscores. Locked after save.</p>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-app-muted">Description</label>
            <Textarea
              rows={4}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="1-3 sentences. Used by the classifier prompt to decide if this reason fits."
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => setCreating(false)}
            disabled={submitting}
            className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submitCreate}
            disabled={submitting || !draft.name.trim() || !draft.key.trim() || !draft.description.trim()}
            className="rounded-control bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-redDeep disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create reason"}
          </button>
        </DialogFooter>
      </Dialog>
    </AppShell>
  )
}
